/**
 * Per-org TanStack Query hooks for the admin org-detail page.
 *
 * Each hook owns ONE BE endpoint from
 * ``ando-agent/src/api/v1/endpoints/platform_admin.py``. The shell
 * (`OrgDetailShell`) composes these on tab switch — TanStack Query
 * caches survive switches so re-opening a tab is instant.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  type AdminConfigureIdpRequest,
  type AdminOrgBillingConfigPatch,
  type AdminOrgBranding,
  type AdminSetMfaPolicyRequest,
  type AdminSetPasswordPolicyRequest,
  type AdminUpdateMemberLimitsRequest,
  type AdminUpdateOrgRequest,
  type OrgRole,
  type ProvisionTier,
  activateInspectMode,
  configureAdminOrgIdp,
  deactivateAdminOrg,
  deprovisionAdminOrg,
  executeAdminOrgProvision,
  getAdminOrg,
  getAdminOrgBillingConfig,
  getAdminOrgBranding,
  getAdminOrgCost,
  getAdminOrgIdp,
  getAdminOrgInvoices,
  getAdminOrgMfaPolicy,
  getAdminOrgPasswordPolicy,
  getAdminOrgProvision,
  getAdminOrgUsage,
  listAdminOrgConnectors,
  listAdminOrgInvites,
  listAdminOrgMembers,
  reactivateAdminOrg,
  reactivateAdminOrgMember,
  removeAdminOrgIdp,
  removeAdminOrgMember,
  requestAdminOrgProvision,
  resendAdminOrgInvite,
  resetAdminOrgBranding,
  retryAdminOrgProvision,
  revokeAdminOrgInvite,
  setAdminOrgConnectorLicense,
  setAdminOrgMemberConnector,
  setAdminOrgMfaPolicy,
  setAdminOrgPasswordPolicy,
  updateAdminOrg,
  updateAdminOrgBillingConfig,
  updateAdminOrgBranding,
  updateAdminOrgMemberLimits,
  updateAdminOrgMemberRole,
} from "@ando/core";

export const adminOrgKeys = {
  all: ["admin", "org"] as const,
  /** List query — invalidated by Edit/Deactivate/Hard-delete/Migrate
   *  mutations. Centralised so every mutation lands on the SAME key
   *  (was 5 raw-string call sites before the June 2026 DRY pass). */
  list: ["admin", "orgs", "list"] as const,
  /** List variant used by :class:`MigrateUserDialog` to populate the
   *  target-org dropdown. Separated from :data:`list` so opening the
   *  migrate dialog doesn't poison the main list cache. */
  migratePicker: ["admin", "orgs", "list", "migrate-picker"] as const,
  org: (orgId: string) => ["admin", "org", orgId] as const,
  members: (orgId: string) => ["admin", "org", orgId, "members"] as const,
  invites: (orgId: string) => ["admin", "org", orgId, "invites"] as const,
  connectors: (orgId: string) => ["admin", "org", orgId, "connectors"] as const,
  usage: (orgId: string) => ["admin", "org", orgId, "usage"] as const,
  cost: (orgId: string, start?: string, end?: string) =>
    ["admin", "org", orgId, "cost", start ?? null, end ?? null] as const,
  invoices: (orgId: string) => ["admin", "org", orgId, "invoices"] as const,
  branding: (orgId: string) => ["admin", "org", orgId, "branding"] as const,
  billingConfig: (orgId: string) => ["admin", "org", orgId, "billing-config"] as const,
  provision: (orgId: string) => ["admin", "org", orgId, "provision"] as const,
  idp: (orgId: string) => ["admin", "org", orgId, "idp"] as const,
  mfa: (orgId: string) => ["admin", "org", orgId, "mfa"] as const,
  password: (orgId: string) => ["admin", "org", orgId, "password-policy"] as const,
};

export function useAdminOrg(orgId: string) {
  return useQuery({
    queryKey: adminOrgKeys.org(orgId),
    queryFn: () => getAdminOrg(orgId),
    staleTime: 60_000,
  });
}

/**
 * Hyper-precise per-org cost drill-down (Jun 5, 2026). Lazy: only fires when
 * the Costs tab mounts. ``start``/``end`` are ISO strings; omit for the BE
 * default window (30d).
 */
export function useAdminOrgCost(
  orgId: string,
  args: { start?: string; end?: string } = {},
  enabled = true,
) {
  return useQuery({
    queryKey: adminOrgKeys.cost(orgId, args.start, args.end),
    queryFn: () => getAdminOrgCost(orgId, args),
    enabled,
    staleTime: 60_000,
  });
}

export function useAdminOrgMembers(orgId: string, enabled = true) {
  return useQuery({
    queryKey: adminOrgKeys.members(orgId),
    queryFn: () => listAdminOrgMembers(orgId),
    enabled,
    staleTime: 30_000,
  });
}

export function useAdminOrgInvites(orgId: string, enabled = true) {
  return useQuery({
    queryKey: adminOrgKeys.invites(orgId),
    queryFn: () => listAdminOrgInvites(orgId),
    enabled,
    staleTime: 30_000,
  });
}

export function useAdminOrgConnectors(orgId: string, enabled = true) {
  return useQuery({
    queryKey: adminOrgKeys.connectors(orgId),
    queryFn: () => listAdminOrgConnectors(orgId),
    enabled,
    staleTime: 30_000,
  });
}

export function useAdminOrgUsage(orgId: string, enabled = true) {
  return useQuery({
    queryKey: adminOrgKeys.usage(orgId),
    queryFn: () => getAdminOrgUsage(orgId),
    enabled,
    staleTime: 60_000,
  });
}

export function useAdminOrgInvoices(orgId: string, enabled = true) {
  return useQuery({
    queryKey: adminOrgKeys.invoices(orgId),
    queryFn: () => getAdminOrgInvoices(orgId),
    enabled,
    staleTime: 60_000,
  });
}

export function useAdminOrgBranding(orgId: string, enabled = true) {
  return useQuery({
    queryKey: adminOrgKeys.branding(orgId),
    queryFn: () => getAdminOrgBranding(orgId),
    enabled,
    staleTime: 60_000,
  });
}

/**
 * The org's billing configuration (collection method, negotiated seat price,
 * no-pay start date).
 *
 * ``staleTime: 0`` unlike its neighbours: this drives money decisions, and a
 * stale "not billed" badge is the kind of thing an admin acts on.
 */
export function useAdminOrgBillingConfig(orgId: string, enabled = true) {
  return useQuery({
    queryKey: adminOrgKeys.billingConfig(orgId),
    queryFn: () => getAdminOrgBillingConfig(orgId),
    enabled,
    staleTime: 0,
  });
}

/**
 * Patch the org's billing configuration.
 *
 * The BE returns the full, re-resolved config, so the response is written
 * straight into the cache instead of triggering a refetch — the form reflects
 * exactly what the server stored (including ``effective_collection_method``
 * and ``is_no_pay``, which are derived there and never recomputed here).
 */
export function useUpdateAdminOrgBillingConfig(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: AdminOrgBillingConfigPatch) => updateAdminOrgBillingConfig(orgId, patch),
    onSuccess: (config) => {
      qc.setQueryData(adminOrgKeys.billingConfig(orgId), config);
      // The org row carries billing_starts_at / the price override too.
      void qc.invalidateQueries({ queryKey: adminOrgKeys.org(orgId) });
    },
  });
}

export function useAdminOrgProvision(orgId: string, enabled = true) {
  return useQuery({
    queryKey: adminOrgKeys.provision(orgId),
    queryFn: () => getAdminOrgProvision(orgId),
    enabled,
    staleTime: 30_000,
  });
}

export function useAdminOrgIdp(orgId: string, enabled = true) {
  return useQuery({
    queryKey: adminOrgKeys.idp(orgId),
    queryFn: () => getAdminOrgIdp(orgId),
    enabled,
    staleTime: 60_000,
  });
}

export function useAdminOrgMfaPolicy(orgId: string, enabled = true) {
  return useQuery({
    queryKey: adminOrgKeys.mfa(orgId),
    queryFn: () => getAdminOrgMfaPolicy(orgId),
    enabled,
    staleTime: 60_000,
  });
}

export function useAdminOrgPasswordPolicy(orgId: string, enabled = true) {
  return useQuery({
    queryKey: adminOrgKeys.password(orgId),
    queryFn: () => getAdminOrgPasswordPolicy(orgId),
    enabled,
    staleTime: 60_000,
  });
}

// ── Mutations ────────────────────────────────────────────────────────────

export function useUpdateAdminOrg(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AdminUpdateOrgRequest) => updateAdminOrg(orgId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminOrgKeys.org(orgId) });
    },
  });
}

export function useDeactivateAdminOrg(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => deactivateAdminOrg(orgId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminOrgKeys.org(orgId) });
    },
  });
}

export function useReactivateAdminOrg(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => reactivateAdminOrg(orgId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminOrgKeys.org(orgId) });
    },
  });
}

export function useUpdateAdminOrgMemberRole(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: OrgRole }) =>
      updateAdminOrgMemberRole(orgId, userId, role),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminOrgKeys.members(orgId) });
    },
  });
}

export function useRemoveAdminOrgMember(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => removeAdminOrgMember(orgId, userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminOrgKeys.members(orgId) });
    },
  });
}

export function useReactivateAdminOrgMember(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => reactivateAdminOrgMember(orgId, userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminOrgKeys.members(orgId) });
    },
  });
}

export function useUpdateAdminOrgMemberLimits(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      body,
    }: {
      userId: string;
      body: AdminUpdateMemberLimitsRequest;
    }) => updateAdminOrgMemberLimits(orgId, userId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminOrgKeys.members(orgId) });
    },
  });
}

export function useRevokeAdminOrgInvite(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (inviteId: string) => revokeAdminOrgInvite(orgId, inviteId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminOrgKeys.invites(orgId) });
    },
  });
}

export function useResendAdminOrgInvite(orgId: string) {
  return useMutation({
    mutationFn: (inviteId: string) => resendAdminOrgInvite(orgId, inviteId),
  });
}

export function useSetAdminOrgConnectorLicense(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      connectorKey,
      is_enabled,
    }: {
      connectorKey: string;
      is_enabled: boolean;
    }) => setAdminOrgConnectorLicense(orgId, connectorKey, { is_enabled }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminOrgKeys.connectors(orgId) });
    },
  });
}

export function useSetAdminOrgMemberConnector(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      connectorKey,
      assigned,
    }: {
      userId: string;
      connectorKey: string;
      assigned: boolean;
    }) => setAdminOrgMemberConnector(orgId, userId, connectorKey, assigned),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminOrgKeys.connectors(orgId) });
    },
  });
}

export function useUpdateAdminOrgBranding(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<AdminOrgBranding>) => updateAdminOrgBranding(orgId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminOrgKeys.branding(orgId) });
    },
  });
}

export function useResetAdminOrgBranding(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => resetAdminOrgBranding(orgId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminOrgKeys.branding(orgId) });
    },
  });
}

export function useRequestAdminOrgProvision(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { tier: ProvisionTier; region: string }) =>
      requestAdminOrgProvision(orgId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminOrgKeys.provision(orgId) });
    },
  });
}

export function useExecuteAdminOrgProvision(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => executeAdminOrgProvision(orgId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminOrgKeys.provision(orgId) });
    },
  });
}

export function useRetryAdminOrgProvision(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => retryAdminOrgProvision(orgId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminOrgKeys.provision(orgId) });
    },
  });
}

export function useDeprovisionAdminOrg(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => deprovisionAdminOrg(orgId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminOrgKeys.provision(orgId) });
    },
  });
}

export function useConfigureAdminOrgIdp(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AdminConfigureIdpRequest) => configureAdminOrgIdp(orgId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminOrgKeys.idp(orgId) });
    },
  });
}

export function useRemoveAdminOrgIdp(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (idpId: string) => removeAdminOrgIdp(orgId, idpId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminOrgKeys.idp(orgId) });
    },
  });
}

export function useSetAdminOrgMfaPolicy(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AdminSetMfaPolicyRequest) => setAdminOrgMfaPolicy(orgId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminOrgKeys.mfa(orgId) });
    },
  });
}

export function useSetAdminOrgPasswordPolicy(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AdminSetPasswordPolicyRequest) => setAdminOrgPasswordPolicy(orgId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminOrgKeys.password(orgId) });
    },
  });
}

/**
 * Scope key for the org Costs → per-user PII reveal. MUST match the BE
 * ``scope_key`` in ``admin_v2.py`` (``get_org_cost`` / ``activate_inspect_mode``)
 * — scopes don't share unlocks, so this string is the contract.
 */
export const ORG_COST_INSPECT_SCOPE = "admin_v2:org_cost";

/**
 * Activate Inspect Mode (audited, 15-min TTL) for the org Costs per-user table.
 * The BE re-checks the gate and starts returning emails, so on success we
 * invalidate the whole cost drill-down (every range) to refetch de-masked.
 */
export function useActivateOrgCostInspect(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reason: string) =>
      activateInspectMode({
        tenant_id: orgId,
        scope_key: ORG_COST_INSPECT_SCOPE,
        reason,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "org", orgId, "cost"] });
    },
  });
}
